import { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { useTranslation } from "../../hooks/useTranslation";
import { useCheckout } from "../../context/CheckoutContext";
import { buildRedirectUrl } from "../../utils/redirectUrlBuilder";

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// SVG circle draw-on for checkmark / X
const drawCircle = keyframes`
  from { stroke-dashoffset: 166; }
  to   { stroke-dashoffset: 0; }
`;

const drawCheck = keyframes`
  from { stroke-dashoffset: 48; }
  to   { stroke-dashoffset: 0; }
`;

const drawX = keyframes`
  from { stroke-dashoffset: 60; }
  to   { stroke-dashoffset: 0; }
`;

const pop = keyframes`
  0%   { transform: scale(0.7); opacity: 0; }
  70%  { transform: scale(1.08); }
  100% { transform: scale(1);   opacity: 1; }
`;

// ─── Layout ──────────────────────────────────────────────────────────────────

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #fff;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.3s ease;
  overflow-y: auto;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 24px 0;
  gap: 10px;
  flex-shrink: 0;
`;

const LogoWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  background: ${(p) => p.$color || "#111"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const LogoInitial = styled.span`
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
`;

const MerchantName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  letter-spacing: -0.01em;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px 48px;
  text-align: center;
  animation: ${slideUp} 0.4s ease 0.1s both;
`;

// ─── Animated SVG icons ───────────────────────────────────────────────────────

const IconCircle = styled.svg`
  width: 72px;
  height: 72px;
  margin-bottom: 28px;
  animation: ${pop} 0.45s ease 0.15s both;
`;

const CirclePath = styled.circle`
  fill: none;
  stroke: ${(p) => p.$color};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: ${drawCircle} 0.55s ease 0.2s forwards;
`;

const CheckPath = styled.polyline`
  fill: none;
  stroke: ${(p) => p.$color};
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: ${drawCheck} 0.3s ease 0.7s forwards;
`;

const XLine1 = styled.line`
  stroke: ${(p) => p.$color};
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-dasharray: 30;
  stroke-dashoffset: 30;
  animation: ${drawX} 0.28s ease 0.7s forwards;
`;

const XLine2 = styled.line`
  stroke: ${(p) => p.$color};
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-dasharray: 30;
  stroke-dashoffset: 30;
  animation: ${drawX} 0.28s ease 0.82s forwards;
`;

const SuccessIcon = () => (
  <IconCircle viewBox="0 0 56 56" aria-hidden="true">
    <CirclePath cx="28" cy="28" r="26" $color="#22c55e" />
    <CheckPath points="16,28 24,36 40,20" $color="#22c55e" />
  </IconCircle>
);

const FailureIcon = () => (
  <IconCircle viewBox="0 0 56 56" aria-hidden="true">
    <CirclePath cx="28" cy="28" r="26" $color="#ef4444" />
    <XLine1 x1="19" y1="19" x2="37" y2="37" $color="#ef4444" />
    <XLine2 x1="37" y1="19" x2="19" y2="37" $color="#ef4444" />
  </IconCircle>
);

// ─── Typography ───────────────────────────────────────────────────────────────

const StatusTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${(p) => (p.$success ? "#111" : "#111")};
  margin: 0 0 10px;
`;

const StatusSub = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 36px;
  line-height: 1.5;
  max-width: 320px;
`;

// ─── CTA button ───────────────────────────────────────────────────────────────

const CTAButton = styled.button`
  width: 100%;
  max-width: 340px;
  padding: 14px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  background: ${(p) => p.$color || (p.$success ? "#111" : "#ef4444")};
  color: #fff;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`;

const Countdown = styled.p`
  margin-top: 14px;
  font-size: 13px;
  color: #9ca3af;
`;

// ─── Footer ───────────────────────────────────────────────────────────────────

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px 24px;
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;

  a {
    color: #9ca3af;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  span + a::before,
  a + a::before {
    content: "·";
    margin-right: 12px;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const NeoStatusScreen = ({
  show,
  onClose,
  setShow,
  status,
  transactionId,
  hostedPageRedirect,
  fallbackRedirectUrl,
  redirectUrl,
  // Allow caller to pass merchant data directly; fall back to CheckoutContext
  merchantName: merchantNameProp,
  brandColor: brandColorProp,
  logoUrl: logoUrlProp,
}) => {
  const { t } = useTranslation();
  const { logoUrl: ctxLogoUrl } = useCheckout();
  const [countdown, setCountdown] = useState(5);

  const isSuccess = status === true || status === "success";
  const logoUrl  = logoUrlProp ?? ctxLogoUrl;
  const brandColor = brandColorProp;
  const merchantName = merchantNameProp;

  const closeModal =
    onClose || (typeof setShow === "function" ? () => setShow(false) : undefined);

  const handleRedirect = () => {
    const finalRedirectUrl = buildRedirectUrl({
      primaryUrl: hostedPageRedirect,
      fallbackUrl: fallbackRedirectUrl || redirectUrl,
      transactionId,
      status: isSuccess ? "success" : "failed",
    });

    if (finalRedirectUrl) {
      window.location.href = finalRedirectUrl;
    } else {
      closeModal?.();
    }
  };

  useEffect(() => {
    if (show) setCountdown(5);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
    if (isSuccess) handleRedirect();
  }, [show, countdown]);

  if (!show) return null;

  const initial = merchantName?.[0]?.toUpperCase() || "M";

  return (
    <Screen role="dialog" aria-modal="true" aria-live="polite">
      {/* Top bar with merchant identity */}
      <TopBar>
        <LogoWrap $color={brandColor}>
          {logoUrl ? (
            <img src={logoUrl} alt={merchantName || "Logo"} />
          ) : (
            <LogoInitial>{initial}</LogoInitial>
          )}
        </LogoWrap>
        {merchantName && <MerchantName>{merchantName}</MerchantName>}
      </TopBar>

      {/* Central content */}
      <Body>
        {isSuccess ? <SuccessIcon /> : <FailureIcon />}

        <StatusTitle $success={isSuccess}>
          {isSuccess
            ? t("modals.transactionStatus.success.title") || "Payment successful"
            : t("modals.transactionStatus.failed.title")  || "Payment failed"}
        </StatusTitle>

        <StatusSub>
          {isSuccess
            ? t("modals.transactionStatus.success.message") ||
              "Your transaction has been approved. Thank you!"
            : t("modals.transactionStatus.failed.message") ||
              "Something went wrong with your payment. Please try again."}
        </StatusSub>

        <CTAButton
          $success={isSuccess}
          $color={brandColor}
          type="button"
          onClick={handleRedirect}
        >
          {t("modals.transactionStatus.backButton") || "Back to website"}
        </CTAButton>

        {isSuccess && (
          <Countdown>
            {t("modals.transactionStatus.autoRedirect", {
              variables: { count: countdown },
            }) || `You'll be automatically redirected in ${countdown} seconds`}
          </Countdown>
        )}
      </Body>

      {/* Footer */}
      <Footer>
        <span>{t("checkout.neo.poweredBy") || "Powered by Altruon"}</span>
        <a href="/terms" target="_blank" rel="noreferrer">
          {t("checkout.neo.termsLink") || "Terms"}
        </a>
        <a href="/privacy" target="_blank" rel="noreferrer">
          {t("checkout.neo.privacyLink") || "Privacy"}
        </a>
      </Footer>
    </Screen>
  );
};

export default NeoStatusScreen;
